import { useCallback, useRef } from "react";

type TriggerProps = Record<string, unknown>;
type AnyRef =
  | ((node: HTMLElement | null) => void)
  | { current: HTMLElement | null }
  | null
  | undefined;

function setRef(ref: AnyRef, node: HTMLElement | null) {
  if (typeof ref === "function") ref(node);
  else if (ref) ref.current = node;
}

// Cached per ref pair so a re-render hands React the same callback, rather
// than detaching and re-attaching both refs on every render.
const mergedRefs = new WeakMap<object, WeakMap<object, AnyRef>>();

function mergeRefs(a: AnyRef, b: AnyRef): AnyRef {
  if (!a) return b;
  if (!b) return a;
  let inner = mergedRefs.get(a);
  if (!inner) mergedRefs.set(a, (inner = new WeakMap()));
  let merged = inner.get(b);
  if (!merged) {
    merged = (node: HTMLElement | null) => {
      setRef(a, node);
      setRef(b, node);
    };
    inner.set(b, merged);
  }
  return merged;
}

/** Two lazy wrappers' trigger bags on ONE element (a tooltip on a popover's
 *  trigger): both refs attach, both handlers run, any other key from `primary`
 *  wins. */
export function mergeTriggerProps(
  secondary: TriggerProps,
  primary: TriggerProps = {},
): TriggerProps {
  const merged: TriggerProps = {
    ...secondary,
    ...primary,
    ref: mergeRefs(secondary.ref as AnyRef, primary.ref as AnyRef),
  };
  for (const [key, a] of Object.entries(secondary)) {
    const b = primary[key];
    if (
      /^on[A-Z]/.test(key) &&
      typeof a === "function" &&
      typeof b === "function"
    ) {
      merged[key] = (...args: unknown[]) => {
        a(...args);
        b(...args);
      };
    }
  }
  return merged;
}

/**
 * The lazy wrappers render a plain trigger until their Base UI chunk loads,
 * then render Base UI's — a different tree, so React remounts the element and
 * the focus that loaded the chunk is dropped on <body>. Tab then restarts from
 * the top of the page. Wrap BOTH branches' trigger props: React detaches the
 * old element's ref before removing it from the DOM, so the detach can still
 * see it holding focus, and the next element attached takes it back.
 */
export function useRefocusOnSwap() {
  const current = useRef<HTMLElement | null>(null);
  const pending = useRef(false);
  const baseRef = useRef<AnyRef>(null);

  const ref = useCallback((node: HTMLElement | null) => {
    if (!node) pending.current = current.current === document.activeElement;
    current.current = node;
    setRef(baseRef.current, node);
    if (node && pending.current) {
      pending.current = false;
      node.focus();
    }
  }, []);

  return (props: TriggerProps): TriggerProps => {
    baseRef.current = props.ref as AnyRef;
    return { ...props, ref };
  };
}
